import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiGlobe, FiGrid, FiLogOut, FiMenu, FiUser, FiX } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const LANGUAGES = [
  { code: 'tr', name: 'Turkce', short: 'TR' },
  { code: 'en', name: 'English', short: 'EN' },
  { code: 'fa', name: 'فارسی', short: 'FA' }
];

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  const authenticated = isAuthenticated();
  const isAdmin = user?.role === 'ADMIN';

  const links = useMemo(
    () => [
      { to: '/', label: t('nav.home') || 'Home' },
      { to: '/hotels', label: t('nav.hotels') || 'Hotels' },
      { to: '/tours', label: t('nav.tours') || 'Tours' },
      { to: '/touristic-places', label: t('nav.places') || 'Places' },
      { to: '/contact', label: t('nav.contact') || t('design.navbar.support') || 'Support' }
    ],
    [t]
  );

  const currentLanguage = LANGUAGES.find((language) => language.code === i18n.language) || LANGUAGES[0];

  const isActive = (path) => location.pathname === path;

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('preferredLanguage', code);
    setIsLanguageOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsAccountOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const navLinkClass = (path) =>
    `rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
      isActive(path)
        ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/10'
        : 'text-slate-700 hover:bg-white/80'
    }`;

  return (
    <nav className="glass-panel mx-auto max-w-7xl px-4 py-3 sm:px-6">
      <div className="flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-700 via-teal-600 to-sky-700 shadow-lg shadow-teal-900/15">
            <img src="/logo.png" alt="RahVan" className="h-8 w-8 object-contain" />
          </div>
          <div>
            <p className="font-serif text-2xl font-semibold text-slate-900">RahVan</p>
            <p className="text-xs uppercase tracking-[0.24em] text-stone-500">{t('design.brandTagline')}</p>
          </div>
        </Link>

        <div className="hidden items-center gap-2 lg:flex">
          {links.map((link) => (
            <Link key={link.to} to={link.to} className={navLinkClass(link.to)}>
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsLanguageOpen((value) => !value)}
              className="btn-ghost px-4 py-2"
            >
              <FiGlobe className="h-4 w-4" />
              {currentLanguage.short}
              <FiChevronDown className={`h-4 w-4 transition-transform ${isLanguageOpen ? 'rotate-180' : ''}`} />
            </button>

            {isLanguageOpen ? (
              <div className="absolute right-0 mt-3 w-44 rounded-3xl border border-stone-200 bg-white/95 p-2 shadow-2xl backdrop-blur">
                {LANGUAGES.map((language) => (
                  <button
                    key={language.code}
                    type="button"
                    onClick={() => changeLanguage(language.code)}
                    className={`flex w-full items-center justify-between rounded-2xl px-3 py-2 text-left text-sm ${
                      language.code === currentLanguage.code ? 'bg-teal-50 text-teal-700' : 'text-slate-700 hover:bg-stone-50'
                    }`}
                  >
                    <span>{language.name}</span>
                    <span className="text-xs uppercase tracking-[0.2em]">{language.short}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {authenticated ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsAccountOpen((value) => !value)}
                className="btn-primary px-4 py-2"
              >
                <FiUser className="h-4 w-4" />
                {user?.name || t('nav.account') || 'Account'}
              </button>

              {isAccountOpen ? (
                <div className="absolute right-0 mt-3 w-56 rounded-3xl border border-stone-200 bg-white/95 p-2 shadow-2xl backdrop-blur">
                  <Link to="/profile" className="flex items-center gap-3 rounded-2xl px-3 py-2 text-sm text-slate-700 hover:bg-stone-50">
                    <FiUser className="h-4 w-4" />
                    {t('design.navbar.profile')}
                  </Link>
                  {isAdmin ? (
                    <Link to="/admin" className="flex items-center gap-3 rounded-2xl px-3 py-2 text-sm text-slate-700 hover:bg-stone-50">
                      <FiGrid className="h-4 w-4" />
                      {t('design.navbar.adminWorkspace')}
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                  >
                    <FiLogOut className="h-4 w-4" />
                    {t('nav.logout') || 'Logout'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {/* <Link to="/login" className="btn-ghost px-4 py-2">
                {t('home.auth.login') || 'Login'}
              </Link>
              <Link to="/register" className="btn-primary px-4 py-2">
                {t('home.auth.register') || 'Register'}
              </Link> */}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setIsMobileMenuOpen((value) => !value)}
          className="flex h-11 w-11 items-center justify-center rounded-2xl border border-stone-200 bg-white/80 text-slate-700 lg:hidden"
        >
          {isMobileMenuOpen ? <FiX className="h-5 w-5" /> : <FiMenu className="h-5 w-5" />}
        </button>
      </div>

      {isMobileMenuOpen ? (
        <div className="mt-4 space-y-4 border-t border-stone-200 pt-4 lg:hidden">
          <div className="grid gap-2">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={navLinkClass(link.to)}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="rounded-3xl border border-stone-200 bg-white/70 p-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">{t('design.navbar.language')}</p>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((language) => (
                <button
                  key={language.code}
                  type="button"
                  onClick={() => changeLanguage(language.code)}
                  className={language.code === currentLanguage.code ? 'btn-primary px-4 py-2' : 'btn-ghost px-4 py-2'}
                >
                  {language.short}
                </button>
              ))}
            </div>
          </div>

          {authenticated ? (
            <div className="grid gap-2">
              <Link to="/profile" className="btn-ghost" onClick={() => setIsMobileMenuOpen(false)}>
                {t('design.navbar.profile')}
              </Link>
              {isAdmin ? (
                <Link to="/admin" className="btn-secondary" onClick={() => setIsMobileMenuOpen(false)}>
                  {t('design.navbar.adminWorkspace')}
                </Link>
              ) : null}
              <button type="button" onClick={handleLogout} className="btn-ghost justify-start text-red-700">
                {t('nav.logout') || 'Logout'}
              </button>
            </div>
          ) : (
            <div className="grid gap-2">
              <Link to="/login" className="btn-ghost" onClick={() => setIsMobileMenuOpen(false)}>
                {t('home.auth.login') || 'Login'}
              </Link>
              <Link to="/register" className="btn-primary" onClick={() => setIsMobileMenuOpen(false)}>
                {t('home.auth.register') || 'Register'}
              </Link>
            </div>
          )}
        </div>
      ) : null}
    </nav>
  );
};

export default Navbar;
